import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { PermissionsResponse } from '@/apis/permissions';
import { getMyPermissions } from '@/apis/permissions';
import { clearPermissionsSession, PERMISSIONS_QUERY_KEY } from '@/lib/permissions-session';
import {
  recordMissingPermissionKey,
  recordPermissionCheck,
  recordPermissionsLoaded,
  recordPermissionsLoadFailure,
  recordPermissionsRefresh,
} from '@/lib/rbac-monitor';
import { setPermissionsForSession } from '@/redux/permissionsSlice';
import {
  getScope as resolveScope,
  hasPermission as resolveHasPermission,
} from '@/utils/permissions';
import type {
  PermissionAction,
  PermissionFeature,
  ResolvedPermissionScope,
} from '@/utils/permissions';

type PermissionsStoreSlice = {
  permissions?: { data?: PermissionsResponse | null; sessionKey?: string | null };
  user?: { role?: string | null; user_id?: string | null; username?: string | null };
  actingOnBehalf?: { isActive?: boolean };
};

const permissionsSessionKey = (
  actorId: string | null,
  role: string | null,
  isActingOnBehalf: boolean
) => `${actorId ?? ''}:${role ?? ''}:${isActingOnBehalf ? 'behalf' : 'self'}`;

const usePermissionsQuery = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const hadPermissionsRef = useRef(false);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const userRole = useSelector((state: PermissionsStoreSlice) => state?.user?.role ?? null);
  const actorId = useSelector((state: PermissionsStoreSlice) => {
    const userId = state?.user?.user_id?.trim();
    const username = state?.user?.username?.trim();
    return userId || username || null;
  });
  const isActingOnBehalf = useSelector(
    (state: PermissionsStoreSlice) => state?.actingOnBehalf?.isActive ?? false
  );
  const isAuthenticated = authStatus === 'authenticated';
  const hasActor = Boolean(actorId);
  const sessionKey = permissionsSessionKey(actorId, userRole, isActingOnBehalf);
  const seenSessionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      seenSessionKeyRef.current = null;
      clearPermissionsSession(dispatch, queryClient);
    }
  }, [dispatch, isAuthenticated, queryClient]);

  const query = useQuery({
    queryKey: [...PERMISSIONS_QUERY_KEY, sessionKey],
    queryFn: ({ signal }) => getMyPermissions(signal),
    enabled: isAuthenticated && Boolean(userRole) && hasActor,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!isAuthenticated || !userRole || !hasActor) return;

    const previousKey = seenSessionKeyRef.current;
    const sessionChanged = previousKey !== null && previousKey !== sessionKey;
    if (previousKey === null || sessionChanged) {
      seenSessionKeyRef.current = sessionKey;
    }
    if (sessionChanged && previousKey) {
      // Drop the previous grants before publishing anything for the new session.
      // query.data here belongs to the new query key, so a cached payload for
      // this session can be stored after the clear in the same turn.
      void queryClient.cancelQueries({ queryKey: [...PERMISSIONS_QUERY_KEY, previousKey] });
      clearPermissionsSession(dispatch, queryClient, previousKey);
    }
    if (!query.data) return;
    dispatch(setPermissionsForSession({ data: query.data, sessionKey }));
    const featureCount = Object.keys(query.data.features ?? {}).length;
    recordPermissionsLoaded(query.data.effective_role ?? null, featureCount);
    if (hadPermissionsRef.current) {
      recordPermissionsRefresh();
    }
    hadPermissionsRef.current = true;
  }, [dispatch, hasActor, isAuthenticated, query.data, queryClient, sessionKey, userRole]);

  useEffect(() => {
    if (query.isError) {
      recordPermissionsLoadFailure();
    }
  }, [query.isError]);

  return query;
};

export interface UsePermissionsResult {
  permissions: PermissionsResponse | null;
  role: string | null;
  effectiveRole: string | null;
  isLoading: boolean;
  isError: boolean;
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean;
  getScope: (feature: PermissionFeature, action: PermissionAction) => ResolvedPermissionScope;
}

/**
 * Public permissions API. Components must use this hook (or `<Can>`) instead of
 * reading the permissions store or backend payload directly.
 */
export const usePermissions = (): UsePermissionsResult => {
  const storedPermissions = useSelector(
    (state: PermissionsStoreSlice) => state?.permissions?.data ?? null
  );
  const storedSessionKey = useSelector(
    (state: PermissionsStoreSlice) => state?.permissions?.sessionKey ?? null
  );
  const userRole = useSelector((state: PermissionsStoreSlice) => state?.user?.role ?? null);
  const actorId = useSelector((state: PermissionsStoreSlice) => {
    const userId = state?.user?.user_id?.trim();
    const username = state?.user?.username?.trim();
    return userId || username || null;
  });
  const isActingOnBehalf = useSelector(
    (state: PermissionsStoreSlice) => state?.actingOnBehalf?.isActive ?? false
  );
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const hasActor = Boolean(actorId);
  const sessionKey = permissionsSessionKey(actorId, userRole, isActingOnBehalf);
  const sessionMatches = hasActor && storedSessionKey === sessionKey;
  const awaitingReplacement = hasActor && storedSessionKey != null && !sessionMatches;
  const query = usePermissionsQuery();
  const permissions =
    !hasActor || awaitingReplacement ? null : (storedPermissions ?? query.data ?? null);
  const isAuthenticated = authStatus === 'authenticated';
  const hasResolvedPermissions =
    hasActor && (sessionMatches || storedPermissions != null || query.data != null);

  const isLoading =
    isAuthenticated &&
    Boolean(userRole) &&
    !query.isError &&
    (!hasActor || awaitingReplacement || !hasResolvedPermissions);

  const hasPermission = useCallback(
    (feature: PermissionFeature, action: PermissionAction): boolean => {
      if (permissions && !permissions.features[feature]) {
        recordMissingPermissionKey(feature);
      }
      recordPermissionCheck();
      return resolveHasPermission(permissions, feature, action);
    },
    [permissions]
  );

  const getScope = useCallback(
    (feature: PermissionFeature, action: PermissionAction) =>
      resolveScope(permissions, feature, action),
    [permissions]
  );

  return {
    permissions,
    role: permissions?.role ?? null,
    effectiveRole: permissions?.effective_role ?? null,
    isLoading,
    isError: query.isError,
    hasPermission,
    getScope,
  };
};

export interface UsePermissionsBootstrapResult {
  isLoading: boolean;
  isError: boolean;
}

/**
 * Auth bootstrap helper. Keeps the authenticated shell behind a spinner until
 * permissions are available. Not for general component use.
 */
export const usePermissionsBootstrap = (): UsePermissionsBootstrapResult => {
  const { isLoading, isError, permissions } = usePermissions();

  return {
    isLoading,
    isError: isError && permissions == null && !isLoading,
  };
};
